import {
  CreateChartOptionsWithConfig,
  PriceHoverData,
  RangeHorizon,
  ResampleIntervals,
} from '@toggle/chart';
import { Entity } from '@toggle/toggle';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ChartAreaWrapperProps } from '~/components/chart-area-wrapper/ChartAreaWrapper';
import { ChartArea } from '~/modules/simple-chart/components/chart-area/ChartArea';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import { useChartTheme } from '~/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import { useTurboChart } from '~/views/turbo-chart/hooks/use-turbo-chart/useTurboChart';
import { ValidChartSettings } from '~/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';

import { MiniLegend } from './components/mini-legend/MiniLegend';
import * as S from './MultiLineChartModule.styles';

export interface TooltipSortConfig {
  field: keyof PriceHoverData;
  desc: boolean;
}
export interface MultiLineChartModuleProps {
  entities: Pick<Entity, 'tag' | 'default_snake' | 'ticker' | 'exchange'>[];
  chartConfig?: ChartAreaWrapperProps['chartConfig'];
  tooltipSortConfig?: TooltipSortConfig;
  source: PosthogTrackingSource;
}

const DEFAULT_SIMPLE_CHART_CONFIG: Partial<
  CreateChartOptionsWithConfig['config']
> = {
  enableZoom: true,
  enableHover: true,
  hoverVariant: 'simple',
};

export const MultiLineChartModule = ({
  entities,
  chartConfig,
  tooltipSortConfig,
  source,
}: MultiLineChartModuleProps) => {
  const initialChartSettings: ValidChartSettings = {
    assets: entities.map(entity => ({
      tag: entity.tag,
      snake: entity.default_snake,
      paneIndex: 0,
    })),
    resample: ResampleIntervals.OneDay,
    horizon: RangeHorizon.OneYear,
    domain: null,
    hidden: undefined,
    paneSettings: {
      0: {
        yAxisType: 'merged',
      },
    },
  };

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { colors } = useChartTheme(['soft', 'multiline']);

  const {
    assetWithError,
    chartData,
    hasError,
    hasEmpty,
    dailyTimeseries,
    chartState,
    retryInitialChartLoad,
    primaryAsset,
    primaryPane,
  } = useTurboChart({
    initialChartSettings,
    shouldUseSearchParams: !initialChartSettings,
  });

  useTranslation(['chart', 'analyze', 'countries']);

  const isLoading = !hasError && !hasEmpty && !dailyTimeseries && !chartData[0];

  if (isLoading) {
    return (
      <S.SkeletonWrapper data-testid="chart-loading">
        <S.SkeletonChartLoader />
      </S.SkeletonWrapper>
    );
  }

  const finalChartConfig = {
    ...DEFAULT_SIMPLE_CHART_CONFIG,
    ...chartConfig,
  };

  if (!primaryAsset || !primaryPane) {
    return null;
  }

  return (
    <S.Container data-testid="multi-line-chart">
      <S.Body>
        <S.StyledChartAreaWrapper
          assetWithError={assetWithError}
          hasError={hasError}
          hasEmpty={hasEmpty}
          colors={colors}
          chartData={chartData}
          chartConfig={finalChartConfig}
          onRetry={retryInitialChartLoad}
          renderChartArea={options => (
            <ChartArea
              canvasRef={canvasRef}
              options={options}
              chartState={chartState}
              primaryAsset={primaryAsset}
              primaryPane={primaryPane}
              tooltipSortConfig={tooltipSortConfig}
              source={source}
            />
          )}
        />
        <MiniLegend
          labels={chartData.map(c => c.entity.formattedTicker)}
          colors={colors.getMulti()}
        />
      </S.Body>
    </S.Container>
  );
};
